import { createContext, ReactNode, useContext, useState, useMemo, useEffect, useCallback } from 'react'
import { useGoToPage } from '../../../routeConfig'
import useInterval from '../../../utils/interval'
import { decodeToken, isTokenActive, useAuthParams, useSaveAuthToken, useClearToken, TokenInfo, useStoredAuthToken } from './utils'

type AuthContextValue = {
  tokenInfo: TokenInfo | null
  isLoggedIn: boolean
  logout: () => void
}

const AuthContext = createContext<AuthContextValue>({isLoggedIn: false, tokenInfo: null, logout: () => {}})

export function AuthProvider({children}: {children: ReactNode}) {
  const saveToken = useSaveAuthToken()
  const clearToken = useClearToken()
  const goToPage = useGoToPage()
  const authParams = useAuthParams()
  const [tokenInfo, setTokenInfo] = useState<TokenInfo| null>(useStoredAuthToken())
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(!!useStoredAuthToken())

  const logout = useCallback(() => {
    clearToken()
    setIsLoggedIn(false)
    setTokenInfo(null)
    goToPage('home')
  }, [clearToken, goToPage])

  const logoutIfTokenExpiredOrMissing = useCallback(() => {
    if ((!!tokenInfo && !isTokenActive(tokenInfo)) || !tokenInfo) {
      logout()
    }
  }, [tokenInfo, logout])

  const authContextValue = useMemo<AuthContextValue>(() => ({isLoggedIn, tokenInfo, logout}), [isLoggedIn, tokenInfo, logout])

  useEffect(() => {
    if (authParams) {
      const token = authParams.token
      const decodedToken = decodeToken(token)

      if (isTokenActive(decodedToken)) {
        setTokenInfo(decodedToken)
        setIsLoggedIn(true)
        saveToken(decodedToken)
      } else {
        logout()
      }
    } else {
      logoutIfTokenExpiredOrMissing()
    }
  }, [authParams, logout, logoutIfTokenExpiredOrMissing, saveToken])

  useInterval(() => {
    logoutIfTokenExpiredOrMissing()
  }, 5000) // checks every five seconds if token is still valid, otherwise logs the user out

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const authContext = useContext(AuthContext)
  return authContext
}
